import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { JOB_REQUEST_STATUSES } from './jobRequestStatus.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

export interface JobRow {
  readonly jobRequestId: string;
  readonly jobNumber: string | null;
  readonly currentStatus: string;
  readonly customerName: string | null;
  readonly territoryId: string | null;
  readonly requestDate: string | null;
  /** The stage it is sitting at, named as the person holding it. */
  readonly waitingOn: string | null;
}

export function SalesJobsListPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { day } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<JobRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [territoryFilter, setTerritoryFilter] = useState('');

  useEffect(() => {
    let alive = true;
    const qs = new URLSearchParams();
    if (statusFilter) qs.set('status', statusFilter);
    if (territoryFilter) qs.set('territoryId', territoryFilter);
    const path = qs.toString() ? `/v1/job-requests?${qs.toString()}` : '/v1/job-requests';
    apiRequest<{ data?: JobRow[] }>(path, { token })
      .then((r) => { if (alive) setRows(r?.data ?? []); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, statusFilter, territoryFilter]);

  return (
    <main aria-label="sales-jobs-list">
      <h1>{t('sales.jobs.title')}</h1>
      <Link to="/sales/jobs/new">{t('sales.jobs.newJob')}</Link>
      <div>
        <label>
          {t('sales.jobs.status')}
          <select aria-label="status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{t('sales.jobs.all')}</option>
            {JOB_REQUEST_STATUSES.map((s) => <option key={s} value={s}>{t(`sales.dash.status.${s}`)}</option>)}
          </select>
        </label>
        <label>
          {t('sales.jobs.territory')}
          <input aria-label="territory-filter" value={territoryFilter} onChange={(e) => setTerritoryFilter(e.target.value)} />
        </label>
      </div>
      {error ? <p role="alert">{error}</p> : null}
      {!rows ? <p>{t('sales.jobs.loading')}</p> : rows.length === 0 ? <p aria-label="empty">{t('sales.jobs.empty')}</p> : (
        <table aria-label="jobs-table">
          <thead><tr><th>{t('sales.jobs.col.code')}</th><th>{t('sales.jobs.col.status')}</th><th>Waiting on</th><th>{t('sales.jobs.col.customer')}</th><th>{t('sales.jobs.col.requestDate')}</th><th /></tr></thead>
          <tbody>
            {rows.map((j) => (
              <tr key={j.jobRequestId} aria-label={`job-row-${j.jobRequestId}`}>
                <td>{j.jobNumber ?? t('sales.jobs.draft')}</td>
                <td>{j.currentStatus}</td>
                {/* The status names a stage; this names the person, which is
                    what anybody chasing the job actually wants to know. */}
                <td data-testid={`waiting-${j.jobRequestId}`}>{j.waitingOn ?? '—'}</td>
                <td>{j.customerName ?? '—'}</td>
                <td>{day(j.requestDate)}</td>
                <td><Link to={`/sales/jobs/${j.jobRequestId}`}>{t('sales.jobs.open')}</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
